import {Injectable} from "@angular/core";
import {File} from "@ionic-native/file";
import {ImageService, JwtHttpClientService} from "@fedelta-pos/data-client";

/**
 * Retrieves the images and caches using the file system
 */

class QueueItem {
    constructor(public imageId: number, public hash: string, public resolve: (value?: any) => void, public reject: (reason?: any) => void) {
    }
}

@Injectable()
export class CachedImageService {

    private readonly fileSystem: CacheFileSystem;

    imagePromiseMap: Map<string, Promise<string>> = new Map();

    constructor(private http: JwtHttpClientService, private imageService: ImageService) {
        console.log("--- CachedImageService");

        this.fileSystem = CachedImageService.createFileSystem();
    }

    /**
     * Retrieve an image from the server and caches the image for further calls
     * @param imageId
     * @param hash
     * @returns {Promise<string>}
     */
    public async getImage(imageId: number, hash: string): Promise<string> {

        // Look for in-progress retrieval of the image
        const key: string = imageId.toString() + ";" + hash;
        let imagePromise: Promise<string> = this.imagePromiseMap.get(key);
        if (imagePromise) {
            return imagePromise;
        }

        imagePromise = (async () => {
            let image: string;
            if (this.fileSystem) {
                try {
                    image = await this.fileSystem.getCachedImage(imageId, hash);
                } catch (error) {
                    image = this.fileSystem.cacheImage(imageId, hash, await this.imageService.getImage(imageId));
                }
            } else {
                image = await this.imageService.getImage(imageId);
            }

            // Map is only used while loading image
            this.imagePromiseMap.delete(key);

            return image;
        })();

        this.imagePromiseMap.set(key, imagePromise);
        return imagePromise;
    }

    private static createFileSystem(): CacheFileSystem {
        return CachedImageService.isCordova() ? new CordovaCacheFileSystem() : new MemoryCacheFileSystem();
    }

    private static isCordova(): boolean {
        return window["cordova"];
    }
}

interface CacheFileSystem {
    /**
     * Returns a Promise that resolves to the cached image or rejects if there is no cached image
     * @param imageId
     * @param hash
     */
    getCachedImage(imageId: number, hash: string): Promise<string>;

    /**
     * Caches the image
     * @param imageId
     * @param hash
     * @param data
     */
    cacheImage(imageId: number, hash: string, data: string): string;
}

class CordovaCacheFileSystem implements CacheFileSystem {
    file: File;
    fullImagePath: string;

    queueItems: QueueItem[] = [];

    pooling: boolean;

    constructor() {
        this.file = new File();
        this.fullImagePath = this.file.dataDirectory + CordovaCacheFileSystem.DIR_IMAGES;

        // noinspection JSIgnoredPromiseFromCall
        this.file.createDir(this.file.dataDirectory, CordovaCacheFileSystem.DIR_IMAGES, false);
    }

    private static readonly DIR_IMAGES: string = "images";

    getCachedImage(imageId: number, requiredHash: string): Promise<string> {
        return new Promise((resolve, reject) => {
            this.queueItems.push(new QueueItem(imageId, requiredHash, resolve, reject));
            this.initNextPool();
        });
    }

    cacheImage(imageId: number, hash: string, data: string): string {
        const fileName: string = imageId.toString() + ".img";
        const fileHashName: string = imageId.toString() + ".hash";

        // noinspection JSIgnoredPromiseFromCall
        this.file.writeFile(this.fullImagePath, fileHashName, hash, {replace: true});
        // noinspection JSIgnoredPromiseFromCall
        this.file.writeFile(this.fullImagePath, fileName, data, {replace: true});
        return data;
    }

    async consumeQueue(): Promise<void> {
        const queueItem: QueueItem = this.queueItems.shift();
        const imageId = queueItem.imageId;
        const requiredHash = queueItem.hash;

        const imgFileName: string = imageId.toString() + ".img";
        const hashFileName: string = imageId.toString() + ".hash";

        try {
            const cachedHash: string = await this.file.readAsText(this.fullImagePath, hashFileName);
            if (cachedHash != requiredHash) {
                // noinspection ExceptionCaughtLocallyJS
                throw("Image hash does not match");
            }
            try {
                queueItem.resolve(await this.file.readAsText(this.fullImagePath, imgFileName));
                this.pooling = false;
                this.initNextPool();
            } catch(error) {
                try {
                    await this.file.removeFile(this.fullImagePath, imgFileName);
                    await this.file.removeFile(this.fullImagePath, hashFileName);
                } catch (ignore) {
                }
                // noinspection ExceptionCaughtLocallyJS
                throw error;
            }
        } catch (error) {
            queueItem.reject(error);
            this.pooling = false;
            this.initNextPool();
        }
    }

    initNextPool(): void {
        if (this.queueItems.length && !this.pooling) {
            this.pooling = true;
            setTimeout(() => this.consumeQueue());
        }
    }
}

class MemoryCacheFileSystem implements CacheFileSystem {

    mapCache: Map<string, string> = new Map<string, string>();

    async getCachedImage(imageId: number, hash: string): Promise<string> {
        const imgKey: string = imageId.toString() + "_img";
        const hashKey: string = imageId.toString() + "_hash";

        const cachedHash: string = this.mapCache.get(hashKey);
        if (!cachedHash || cachedHash != hash) {
            if (!cachedHash) {
                this.mapCache.delete(hashKey);
                this.mapCache.delete(imgKey);
            }
            throw "image not cached";
        }
        const cachedImage: string = this.mapCache.get(imgKey);
        if (!cachedImage) {
            throw "image not cached";
        }
        return cachedImage;
    }

    cacheImage(imageId: number, hash: string, data: string): string {
        const imgKey: string = imageId.toString() + "_img";
        const hashKey: string = imageId.toString() + "_hash";

        try {
            this.mapCache.set(imgKey, data);
            this.mapCache.set(hashKey, hash);
            return data;
        } catch (error) {
            console.log("Failed to cache the image");
            console.log(error);
            return undefined;
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/ui-elements/image/cached-image.service.ts